/**
 * useBackgroundJobs — tracks active background jobs across page navigations.
 *
 * Sources:
 *  - `ai_media:progress`       — TemplateProgressReporter emits this at every stage
 *  - `deal:analysis_progress`  — ExtractionAgent emits this at every stage transition
 *  - `notification:job_event`  — transient lifecycle events (queued/completed/failed)
 *  - `notification:new_batch`  — stored completion/failure notifications
 *
 * State is persisted in sessionStorage so it survives in-app navigation.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { notificationRealtime } from "./notificationRealtime";
import { fetchActiveJobs, fetchAnalysisJobStatus } from "./activeJobsSync";

// ── Types ─────────────────────────────────────────────────────────────────────

export type JobKind = "deal_analysis" | "ai_media";

export type JobStatus = "queued" | "started" | "running" | "completed" | "failed";

export type BackgroundJob = {
  jobKey: string;
  kind: JobKind;
  status: JobStatus;
  title: string;
  stage: string;
  stageLabelHuman: string;
  progress: number;       // 0-100
  startedAt: number;
  updatedAt: number;
  dealId?: string;
  jobId?: string;
  sessionId?: string;
  assetType?: string;
  /** Latest activity message from SSE ticker */
  activityMessage?: string;
  /** Friendly server message when a stuck job was auto-reset (stale reaping) */
  errorMessage?: string;
};

// ── Stage metadata ────────────────────────────────────────────────────────────

// AI media stages → {label, progress}
const AI_MEDIA_STAGE_META: Record<string, { label: string; progress: number }> = {
  started:              { label: "Starting…",              progress: 2  },
  quota_check:          { label: "Checking quota",         progress: 6  },
  session_start:        { label: "Loading session…",       progress: 12 },
  session_ready:        { label: "Ready",                  progress: 16 },
  ocr_scanning:         { label: "Scanning upload…",       progress: 22 },
  parsing:              { label: "Reading brief…",         progress: 30 },
  consolidating:        { label: "Combining details…",     progress: 38 },
  followup:             { label: "Needs your input",       progress: 42 },
  building_prompt:      { label: "Writing prompt…",        progress: 50 },
  generating_itinerary: { label: "Writing itinerary…",     progress: 58 },
  rendering_html:       { label: "Building pages…",        progress: 76 },
  generating_image:     { label: "Painting flyer…",        progress: 72 },
  generating_video:     { label: "Rendering video…",       progress: 72 },
  generating_slides:    { label: "Building slides…",       progress: 72 },
  itinerary_render:     { label: "Composing itinerary…",   progress: 80 },
  uploading:            { label: "Saving…",                progress: 93 },
  complete:             { label: "Done ✓",                 progress: 100},
  error:                { label: "Failed",                 progress: 0  },
};

// Deal analysis stages → {label, progress}
// Mirrors backend/app/modules/deals/schemas/sse.py:DealAnalysisStage — keep in sync.
const DEAL_STAGE_META: Record<string, { label: string; progress: number }> = {
  queued:          { label: "Queued for Gullivr AI review",              progress: 5  },
  ocr_running:     { label: "Reading your travel listing…",              progress: 25 },
  ocr_completed:   { label: "Listing read",                              progress: 35 },
  llm_running:     { label: "Structuring your deal for B2B buyers…",     progress: 60 },
  llm_completed:   { label: "Deal details structured",                   progress: 72 },
  index_running:   { label: "Preparing your Gullivr listing…",           progress: 85 },
  index_completed: { label: "Draft ready on Gullivr",                    progress: 95 },
  completed:       { label: "Saved as draft — publish to go live ✓",     progress: 100},
  failed:          { label: "Analysis failed",                          progress: 0  },
};

function aiMediaMeta(stage: string) {
  return AI_MEDIA_STAGE_META[stage] ?? { label: stage, progress: 50 };
}
function dealMeta(stage: string) {
  return DEAL_STAGE_META[stage] ?? { label: stage, progress: 50 };
}

const TERMINAL_STATUSES = new Set<JobStatus>(["completed", "failed"]);
function stageToStatus(stage: string, kind: JobKind): JobStatus {
  if (stage === "completed" || stage === "complete") return "completed";
  if (stage === "failed" || stage === "error")       return "failed";
  // followup = generation paused for user input; treat as completed so the bar
  // auto-dismisses after 10s and server re-sync doesn't reset it back to "running"
  if (kind === "ai_media" && stage === "followup")   return "completed";
  if (stage === "queued")                            return "queued";
  if (kind === "ai_media" && stage === "started")    return "started";
  return "running";
}

// ── Session storage helpers ───────────────────────────────────────────────────

const STORAGE_KEY = "bg_jobs_v2";
const JOB_TTL_MS  = 2 * 60 * 60 * 1000; // 2 hours

function loadJobs(): Map<string, BackgroundJob> {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return new Map();
    const arr = JSON.parse(raw) as BackgroundJob[];
    const now = Date.now();
    return new Map(
      arr
        .filter((j) => now - j.startedAt < JOB_TTL_MS)
        .map((j) => [j.jobKey, j]),
    );
  } catch {
    return new Map();
  }
}

function saveJobs(map: Map<string, BackgroundJob>): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify([...map.values()]));
  } catch { /* quota */ }
}

// ── Hook ─────────────────────────────────────────────────────────────────────

export function useBackgroundJobs() {
  const [jobs, setJobs] = useState<Map<string, BackgroundJob>>(loadJobs);
  const jobsRef = useRef(jobs);
  useEffect(() => { jobsRef.current = jobs; }, [jobs]);

  const upsert = useCallback((key: string, patch: Partial<BackgroundJob> & { kind: JobKind }) => {
    setJobs((prev) => {
      const existing = prev.get(key);
      const now = Date.now();
      const next = new Map(prev);
      next.set(key, {
        jobKey: key,
        stage: "",
        stageLabelHuman: "",
        progress: 0,
        startedAt: now,
        title: "",
        status: "queued",
        ...existing,
        ...patch,
        updatedAt: now,
      } as BackgroundJob);
      saveJobs(next);
      return next;
    });
  }, []);

  // Subscribe to the shared notifications WS and extract job progress from all event types
  useEffect(() => {
    return notificationRealtime.subscribe((msg) => {
      // ── ai_media:progress ───────────────────────────────────────────────
      if (msg.type === "ai_media:progress") {
        const m = msg as { type: string; stage?: string; progress?: number; message?: string; asset_type?: string; template_name?: string; session_id?: string };
        const sessionId = m.session_id ?? "";
        if (!sessionId) return;
        const stage = m.stage ?? "";
        const meta  = aiMediaMeta(stage);
        const pct   = typeof m.progress === "number" ? m.progress : meta.progress;

        // Throttle ticker ticks: skip if stage unchanged AND progress moved < 5 pts.
        // Terminal stages (complete/error) always go through.
        const existing = jobsRef.current.get(`media:${sessionId}`);
        const isTerminal = stage === "complete" || stage === "completed" || stage === "error" || stage === "failed";
        if (!isTerminal && existing && existing.stage === stage && Math.abs((existing.progress ?? 0) - pct) < 5) {
          return;
        }

        upsert(`media:${sessionId}`, {
          kind: "ai_media",
          status: stageToStatus(stage, "ai_media"),
          stage,
          stageLabelHuman: meta.label,
          progress: pct,
          sessionId,
          assetType: m.asset_type ?? "",
          title: m.template_name ?? m.asset_type ?? "Asset",
          activityMessage: m.message ?? undefined,
        });
        return;
      }

      // ── deal:analysis_progress ──────────────────────────────────────────
      if (msg.type === "deal:analysis_progress") {
        const m = msg as { type: string; stage?: string; job_id?: string; deal_id?: string; message?: string };
        const dealId = m.deal_id ?? "";
        if (!dealId) return;
        const stage = m.stage ?? "";
        const meta  = dealMeta(stage);
        upsert(`deal:${dealId}`, {
          kind: "deal_analysis",
          status: stageToStatus(stage, "deal_analysis"),
          stage,
          stageLabelHuman: meta.label,
          progress: meta.progress,
          dealId,
          jobId: m.job_id ?? "",
        });
        return;
      }

      // ── notification:job_event (transient lifecycle events) ─────────────
      if (msg.type === "notification:job_event") {
        const m = msg as { type: string; notif_type?: string; title?: string; metadata?: Record<string, unknown> };
        const notifType = m.notif_type ?? "";
        const meta2 = (m.metadata ?? {}) as Record<string, unknown>;

        if (notifType.startsWith("deal.analysis.")) {
          const dealId = String(meta2.deal_id ?? "");
          if (!dealId) return;
          const stage = notifType.replace("deal.analysis.", "");
          const sm = dealMeta(stage === "queued" ? "queued" : stage === "completed" ? "completed" : stage === "failed" ? "failed" : stage);
          upsert(`deal:${dealId}`, {
            kind: "deal_analysis",
            status: stageToStatus(stage, "deal_analysis"),
            stage,
            stageLabelHuman: sm.label,
            progress: sm.progress,
            dealId,
            jobId: String(meta2.job_id ?? ""),
            title: String(meta2.deal_title ?? dealId),
          });
        }

        if (notifType.startsWith("ai_media.generation.")) {
          const sessionId = String(meta2.session_id ?? "");
          if (!sessionId) return;
          const stage = notifType === "ai_media.generation.started" ? "started" : notifType === "ai_media.generation.failed" ? "error" : "complete";
          const sm = aiMediaMeta(stage);
          upsert(`media:${sessionId}`, {
            kind: "ai_media",
            status: stageToStatus(stage, "ai_media"),
            stage,
            stageLabelHuman: sm.label,
            progress: sm.progress,
            sessionId,
            assetType: String(meta2.asset_type ?? ""),
            title: String(meta2.template_name ?? meta2.asset_type ?? "Asset"),
          });
        }
        return;
      }

      // ── notification:new_batch (stored completion/failure notifications) ─
      // Skip terminal jobs — these are historical batch notifications re-delivered
      // on WS reconnect. Upserting them would reset the 10s auto-dismiss timer.
      // Real-time completion is handled via deal:analysis_progress / notification:job_event.
      if (msg.type === "notification:new_batch") {
        const batch = ((msg as Record<string, unknown>).notifications ?? []) as Array<{
          type?: string; metadata?: Record<string, unknown>;
        }>;
        for (const row of batch) {
          const t = String(row.type ?? "");
          const meta2 = row.metadata ?? {};

          // Only process in-progress deal events — skip completed/failed
          if (t.startsWith("deal.analysis.") && t !== "deal.analysis.completed" && t !== "deal.analysis.failed") {
            const dealId = String(meta2.deal_id ?? "");
            if (!dealId) continue;
            const stage = t.replace("deal.analysis.", "");
            const sm = dealMeta(stage);
            upsert(`deal:${dealId}`, {
              kind: "deal_analysis",
              status: stageToStatus(stage, "deal_analysis"),
              stage,
              stageLabelHuman: sm.label,
              progress: sm.progress,
              dealId,
              jobId: String(meta2.job_id ?? ""),
              title: String(meta2.deal_title ?? dealId),
            });
          }

          // Skip media completion events from batch — handled by ai_media:progress in real-time
        }
      }
    });
  }, [upsert]);

  const trackDealAnalysis = useCallback(
    (dealId: string, jobId: string, dealTitle = "") => {
      upsert(`deal:${dealId}`, {
        kind: "deal_analysis",
        status: "queued",
        stage: "queued",
        stageLabelHuman: "Queued…",
        progress: 5,
        title: dealTitle || dealId,
        dealId,
        jobId,
        startedAt: Date.now(),
      });
    },
    [upsert],
  );

  const trackAiMedia = useCallback(
    (sessionId: string, assetType: string, templateName = "") => {
      upsert(`media:${sessionId}`, {
        kind: "ai_media",
        status: "started",
        stage: "started",
        stageLabelHuman: "Starting…",
        progress: 2,
        title: templateName || assetType,
        sessionId,
        assetType,
        startedAt: Date.now(),
      });
    },
    [upsert],
  );

  /**
   * Fetch /api/v1/jobs/active and seed the job store.
   * Idempotent — terminal jobs already in store are only updated, never re-added.
   */
  const syncFromServer = useCallback(async () => {
    const serverJobs = await fetchActiveJobs();

    // Keys the server currently reports (active OR within the 5-min completion
    // window). Used below to detect locally-stuck jobs the feed no longer covers.
    const seenKeys = new Set<string>();
    for (const j of serverJobs) {
      if (j.job_type === "deal_analysis") seenKeys.add(`deal:${j.deal_id ?? j.job_id}`);
      else if (j.session_id) seenKeys.add(`media:${j.session_id}`);
    }

    for (const j of serverJobs) {
      // If server says terminal but we have a stale non-terminal entry (e.g. stuck
      // as "queued" because Redis was down and WS events never arrived), update it
      // so the auto-dismiss fires and CreateDealPage can clear localStorage.
      if (j.status === "completed" || j.status === "failed") {
        const staleKey = j.job_type === "deal_analysis"
          ? `deal:${j.deal_id ?? j.job_id}`
          : j.session_id ? `media:${j.session_id}` : null;
        if (staleKey) {
          const existing = jobsRef.current.get(staleKey);
          if (existing && !TERMINAL_STATUSES.has(existing.status)) {
            upsert(staleKey, {
              ...existing,
              kind: existing.kind,
              status: j.status as JobStatus,
              progress: j.status === "completed" ? 100 : existing.progress,
              stage: j.status,
              stageLabelHuman: j.status === "completed" ? "Done ✓" : "Failed",
              errorMessage: j.user_message ?? existing.errorMessage,
            });
          }
        }
        continue;
      }

      if (j.job_type === "deal_analysis") {
        const key = `deal:${j.deal_id ?? j.job_id}`;
        const existing = jobsRef.current.get(key);
        const stage = j.stage || j.status;
        const sm = dealMeta(stage);
        const serverProgress = j.progress ?? sm.progress;
        // Don't regress progress if WS already pushed a newer update.
        const progress = existing ? Math.max(existing.progress, serverProgress) : serverProgress;
        upsert(key, {
          kind: "deal_analysis",
          status: stageToStatus(stage, "deal_analysis"),
          stage: existing && existing.progress > serverProgress ? existing.stage : stage,
          stageLabelHuman: existing && existing.progress > serverProgress ? existing.stageLabelHuman : sm.label,
          progress,
          dealId: j.deal_id ?? "",
          jobId: j.job_id,
          title: j.title || j.deal_id || "Deal",
          startedAt: j.started_at ? new Date(j.started_at).getTime() : Date.now(),
        });
      } else {
        const sid = j.session_id;
        if (!sid) continue;
        const key = `media:${sid}`;
        const existing = jobsRef.current.get(key);
        const stage = j.stage || j.status;
        const sm = aiMediaMeta(stage);
        const serverProgress = j.progress ?? sm.progress;
        const progress = existing ? Math.max(existing.progress, serverProgress) : serverProgress;
        upsert(key, {
          kind: "ai_media",
          status: stageToStatus(stage, "ai_media"),
          stage: existing && existing.progress > serverProgress ? existing.stage : stage,
          stageLabelHuman: existing && existing.progress > serverProgress ? existing.stageLabelHuman : sm.label,
          progress,
          sessionId: sid,
          assetType: j.asset_type ?? j.job_type ?? "",
          title: j.title || j.asset_type || "Asset",
          startedAt: j.started_at ? new Date(j.started_at).getTime() : Date.now(),
        });
      }
    }

    // ── Reconcile locally-tracked jobs the active feed no longer covers ────────
    // A deal_analysis entry can get stranded as "running" if its completion event
    // was missed (WS drop / tab backgrounded) AND the server no longer lists it as
    // active (finished + aged past the completion window). The trigger is purely
    // server-driven — the job's absence from /jobs/active — and the decision is the
    // server's: we ask the backend for the authoritative status by job_id and only
    // mark terminal when the server says so. No client-side timing/staleness logic.
    const stuck = [...jobsRef.current.values()].filter(
      (job) =>
        job.kind === "deal_analysis" &&
        !TERMINAL_STATUSES.has(job.status) &&
        !seenKeys.has(job.jobKey) &&
        !!job.jobId,
    );
    await Promise.all(
      stuck.map(async (job) => {
        const real = await fetchAnalysisJobStatus(job.jobId!);
        if (!real) return;
        if (real.status === "completed" || real.status === "failed") {
          upsert(job.jobKey, {
            ...job,
            kind: job.kind,
            status: real.status as JobStatus,
            progress: real.status === "completed" ? 100 : job.progress,
            stage: real.status,
            stageLabelHuman: real.status === "completed" ? "Done ✓" : "Failed",
          });
        }
      }),
    );
  }, [upsert]);

  // Sync on mount (covers login + page refresh)
  useEffect(() => {
    syncFromServer();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-sync when WebSocket reconnects (catches events missed while offline)
  useEffect(() => {
    return notificationRealtime.subscribe((msg) => {
      if ((msg as { type: string }).type === "notification:connected") {
        syncFromServer();
      }
    });
  }, [syncFromServer]);

  // Re-sync when app returns to foreground (Android/iOS back, browser tab switch)
  useEffect(() => {
    const handler = () => {
      if (document.visibilityState === "visible") syncFromServer();
    };
    document.addEventListener("visibilitychange", handler);
    return () => document.removeEventListener("visibilitychange", handler);
  }, [syncFromServer]);

  // Poll every 5 s while there are active (non-terminal) jobs so stuck bars recover
  // even when WS events are dropped.
  useEffect(() => {
    const id = window.setInterval(() => {
      const hasActive = [...jobsRef.current.values()].some(
        (j) => !TERMINAL_STATUSES.has(j.status),
      );
      if (hasActive) syncFromServer();
    }, 5_000);
    return () => window.clearInterval(id);
  }, [syncFromServer]);

  /** Jobs that are still running or finished within the last 10 s */
  const activeJobs = useCallback((): BackgroundJob[] => {
    const now = Date.now();
    return [...jobsRef.current.values()].filter((j) => {
      if (!TERMINAL_STATUSES.has(j.status)) return true;
      return now - j.updatedAt < 10_000;
    });
  }, []);

  /**
   * Remove a job from the bar. If the job is still running the next WS event
   * will re-add it automatically via upsert, so dismiss is always reversible.
   */
  const dismiss = useCallback((jobKey: string) => {
    setJobs((prev) => {
      const next = new Map(prev);
      next.delete(jobKey);
      saveJobs(next);
      return next;
    });
  }, []);

  return { jobs, activeJobs, trackDealAnalysis, trackAiMedia, dismiss, syncFromServer };
}
